import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { languages } from "locales/languages"
import { DEVICE_ID_DERMATOSCOPE } from "containers/Call/types/types"
import { UploadedDocumentType } from "containers/Survey/types"
import styles from "./styles.module.scss"
import { Buttons, Card } from "components/Card/Card"
import { Button } from "components/Button"
import { useDispatch, useSelector } from "react-redux"
import actionsUpload from "containers/Upload/services/actions"
import actionsSurvey from "containers/Survey/services/actions"
import { Message } from "types/redux"
import PreviousButton from "components/Previous/PreviousButton"
import {
  STATUS_REJECTED,
  STATUS_RESOLVED,
} from "containers/Upload/services/constants"
import { Diagnosis } from "containers/Diagnosis/Diagnosis"
import { awaitingDevice, getDeviceId, getDeviceInfo } from "lib/video"
import useDevices from "containers/Call/hooks/useDevices"
import { Title } from "components/Title/Title"
import { cdn } from "core/cdn"
import { useStepper } from "components/Steps/hooks"
import { Steps } from "components/Steps"
import { PhotoCarousel } from "components/PhotoCarousel"

const COUNTDOWN_SECONDS = 5
// A burst is taken so the patient can keep the sharpest shot, matching the
// general-medicine capture.
const BURST_SIZE = 3
const BURST_INTERVAL_MS = 666

const getDermatoscopeDevice = () => {
  return awaitingDevice(DEVICE_ID_DERMATOSCOPE, () => {
    console.log("En attente du dermatoscope...")
  })
}

export const DermatoscopeVideo: React.FC = () => {
  const navigate = useNavigate()
  const [message, setMessage] = useState<Message | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [countdown, setCountdown] = useState<number | null>(null)
  const [capturedImages, setCapturedImages] = useState<string[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const capturedImage = capturedImages[selectedIndex] ?? null
  const [isDeviceReady, setIsDeviceReady] = useState(false)
  const [isWaitingDevice, setIsWaitingDevice] = useState(true)
  const devices = useDevices()
  const { current, insideStep, totalInsideSteps, totalSteps } = useStepper()

  const [step, setStep] = useState<{
    status:
      | "tutorial"
      | "capture"
      | "select"
      | "sending"
      | "sent"
      | "error"
      | "finish"
    title: string
  }>({ status: "tutorial", title: languages.dermatoVideoCaptureTitle })

  const {
    loading: submitDocumentLoading,
    status: submitDocumentStatus,
    documents: documentsUploaded,
  } = useSelector((state: any) => state.upload)
  const dispatch = useDispatch()

  const stopVideoStream = useCallback(() => {
    const stream =
      streamRef.current ?? (videoRef.current?.srcObject as MediaStream | null)
    stream?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  const onDermatoScopeOff = useCallback(async () => {
    setIsWaitingDevice(true)
    setIsDeviceReady(false)
    stopVideoStream()
    if (step.status === "capture") {
      setStep({
        status: "tutorial",
        title: languages.dermatoVideoCaptureTitle,
      })
    }
  }, [step, stopVideoStream])

  useEffect(() => {
    const dermato = getDeviceId(devices, DEVICE_ID_DERMATOSCOPE)
    if (!dermato) {
      onDermatoScopeOff()
    }
  }, [devices, onDermatoScopeOff])

  const setupDevice = useCallback(async (device: MediaDeviceInfo) => {
    setIsWaitingDevice(false)
    // Récupérer le flux vidéo du dermatoscope spécifiquement
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: { exact: device.deviceId },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    })
    streamRef.current = mediaStream
    if (videoRef.current) {
      videoRef.current.srcObject = mediaStream
      setIsDeviceReady(true)
    }
  }, [])

  const [isShooting, setIsShooting] = useState(false)
  const burstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelBurst = useCallback(() => {
    if (burstTimerRef.current) clearTimeout(burstTimerRef.current)
    burstTimerRef.current = null
    setIsShooting(false)
  }, [])

  const initializeVideo = useCallback(async () => {
    stopVideoStream()
    cancelBurst()
    if (timerCountdown) clearTimeout(timerCountdown)
    setTimerCountdown(null)
    setIsDeviceReady(false)
    setCapturedImages([])
    setSelectedIndex(0)
    try {
      const dermato = await getDermatoscopeDevice()
      if (dermato) setupDevice(dermato)
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupDevice, stopVideoStream, cancelBurst])

  // Gérer le compte à rebours
  const [timerCountdown, setTimerCountdown] = useState<NodeJS.Timeout | null>(
    null,
  )

  useEffect(() => {
    if (countdown === null || countdown < 0) return
    const timer = setTimeout(() => {
      if (countdown === 0) {
        capturePhoto()
      } else {
        setCountdown(countdown - 1)
      }
    }, 1000)
    setTimerCountdown(timer)
    return () => clearTimeout(timerCountdown)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown])

  // Démarrer le compte à rebours
  const startCountdown = () => {
    setStep({
      status: "capture",
      title: languages.dermatoVideoCaptureEnsureVisible,
    })
    setCountdown(COUNTDOWN_SECONDS)
  }

  const [isFlashing, setIsFlashing] = useState(false)

  // Capturer une image du flux vidéo
  const grabFrame = (): string | null => {
    if (!videoRef.current || !videoRef.current.videoWidth) return null

    const canvas = document.createElement("canvas")
    canvas.width = videoRef.current.videoWidth
    canvas.height = videoRef.current.videoHeight

    const context = canvas.getContext("2d")
    if (!context) return null

    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL("image/png")
  }

  const flash = () => {
    setIsFlashing(true)
    setTimeout(() => setIsFlashing(false), 400)
  }

  // Capturer une rafale de photos du flux vidéo.
  // Every shot is taken before any state change, because showing the shots
  // unmounts the video element the frames are read from.
  const capturePhoto = () => {
    if (!videoRef.current) return
    setCountdown(null)
    setIsShooting(true)
    setStep({
      status: "capture",
      title: languages.dermatoVideoCaptureShooting,
    })

    const shots: string[] = []

    const keepShots = () => {
      burstTimerRef.current = null
      setIsShooting(false)
      if (!shots.length) return
      flash()
      setCapturedImages(shots)
      setSelectedIndex(0)
      setStep({
        status: "select",
        title: languages.dermatoVideoCaptureSelectBest,
      })
    }

    const grab = () => {
      const frame = grabFrame()
      // The dermatoscope was turned off mid-burst: keep whatever was taken.
      if (!frame) {
        keepShots()
        return
      }
      shots.push(frame)
      if (shots.length >= BURST_SIZE) {
        keepShots()
        return
      }
      burstTimerRef.current = setTimeout(grab, BURST_INTERVAL_MS)
    }

    grab()
  }

  // Reprendre une photo
  const retakePhoto = () => {
    setStep({
      status: "tutorial",
      title: languages.dermatoVideoCaptureTitle,
    })
  }

  // Envoyer la photo
  const sendPhoto = () => {
    if (!capturedImage) {
      return
    }

    const doc: UploadedDocumentType = {
      name: languages.dermatoVideoCapturePhotoName,
      file: [capturedImage],
      medicalType: "picture",
    }

    dispatch(actionsUpload.submitDocument(doc))
  }

  const previousStep = () => {
    stopVideoStream()
    setStep({
      status: "tutorial",
      title: languages.dermatoVideoCaptureTitle,
    })
    dispatch(actionsSurvey.previous())
    navigate("/survey")
  }
  const forceFinishStep = useCallback(() => {
    stopVideoStream()
    setStep({
      status: "tutorial",
      title: languages.dermatoVideoCaptureTitle,
    })
    dispatch(actionsSurvey.next())
    navigate("/survey")
  }, [dispatch, navigate, stopVideoStream])

  // Passer l'étape
  const finishStep = useCallback(async () => {
    setMessage(null)
    // Release our own hold on the dermatoscope before checking whether it's off,
    // otherwise the device-off check can find it still "in use" by this tab.
    stopVideoStream()
    try {
      const device = await getDeviceInfo().then((devices) => {
        return getDeviceId(devices, DEVICE_ID_DERMATOSCOPE)
      })
      if (device) {
        setStep({
          status: "finish",
          title: languages.dermatoVideoCaptureTurnOffDevice,
        })
      } else {
        forceFinishStep()
      }
    } catch (e) {
      console.error(e)
      forceFinishStep()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate, forceFinishStep, stopVideoStream])

  // Hook pour gérer les changements de statut de l'upload
  useEffect(() => {
    if (submitDocumentLoading) {
      setStep({
        status: "sending",
        title: languages.dermatoVideoCaptureSending,
      })
    }
    if (submitDocumentStatus === STATUS_RESOLVED) {
      if (capturedImage) dispatch(actionsUpload.addDermatoPhoto(capturedImage))
      dispatch(actionsUpload.resetStatus())
      setStep({
        status: "sent",
        title: languages.dermatoVideoCaptureSuccess,
      })
    }
    if (submitDocumentStatus === STATUS_REJECTED) {
      dispatch(actionsUpload.resetStatus())
      setStep({
        status: "select",
        title: languages.dermatoVideoCaptureError,
      })
      setMessage({
        type: "error",
        text: languages.dermatoVideoCaptureError,
      })
    }
  }, [submitDocumentStatus, submitDocumentLoading, dispatch, documentsUploaded])

  // Hook pour gérer les transitions d'état
  useEffect(() => {
    if (step.status === "sent") {
      dispatch(actionsSurvey.getDocuments())
    }
    if (step.status === "tutorial") {
      initializeVideo()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, finishStep, initializeVideo])

  useEffect(() => {
    return () => {
      cancelBurst()
      stopVideoStream()
    }
  }, [cancelBurst, stopVideoStream])

  const percent = (countdown: number) => {
    return 100 - Math.round((countdown / COUNTDOWN_SECONDS) * 100)
  }

  if (step.status === "finish") {
    return (
      <Diagnosis
        onStop={() => retakePhoto()}
        onFinish={() => forceFinishStep()}
      />
    )
  }

  return (
    <Card
      title={step.title}
      titleSize={step.status === "select" ? undefined : "small"}
      message={message}
    >
      <Steps
        current={current}
        insideStep={insideStep}
        totalInsideSteps={totalInsideSteps}
        totalSteps={totalSteps}
      />
      <div
        className={`${styles.flashOverlay} ${isFlashing ? styles.flashOverlayActive : ""}`}
      />
      <PreviousButton
        onClick={() => {
          if (step.status === "tutorial") {
            previousStep()
          } else {
            retakePhoto()
          }
        }}
      />
      {step.status === "select" ? (
        <div className={styles.selectContent}>
          <PhotoCarousel
            images={capturedImages}
            selectedIndex={selectedIndex}
            onSelect={setSelectedIndex}
            orientation="landscape"
            alt={languages.dermatoVideoCapturePhotoName}
          />
        </div>
      ) : (
        <div className={styles.content}>
          {/* Vidéo tutoriel à gauche */}
          {step.status === "tutorial" && (
            <div className={styles.tutorialSection}>
              <h2>{languages.dermatoVideoCaptureTutorialLabel}</h2>
              <div className={styles.videoContainer}>
                <video
                  className={styles.tutorialVideo}
                  controls
                  autoPlay
                  src={cdn("/videos/tutos/dermatoscope.mp4")}
                />
              </div>
            </div>
          )}

          {/* Retour vidéo du dermatoscope à droite */}
          <div className={styles.dermatoscopeSection}>
            {step.status === "tutorial" && <h2>{languages.dermatoVideoCaptureVideoFeedLabel}</h2>}
            <div className={styles.videoContainer}>
              {isWaitingDevice &&
                (step.status === "tutorial" || step.status === "capture") && (
                <div className={styles.waitingMessage}>
                  <Title
                    text={languages.dermatoVideoCaptureTakeAndTurnOnDevice}
                    size="small"
                  />
                  <img
                    height="200px"
                    src={cdn("icons/dermatoscope.png")}
                    alt="Dermatoscope"
                  />
                </div>
              )}
              {capturedImage ? (
                <img
                  src={capturedImage}
                  alt="Captured"
                  className={styles.capturedImage}
                />
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className={
                    isWaitingDevice
                      ? styles.dermatoscopeVideoWaiting
                      : styles.dermatoscopeVideo
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
      <Buttons>
        {step.status === "capture" && (
          <Button className={styles.buttonCountdown} disabled={true}>
            <div
              className={styles.progressBar}
              style={{ width: `${isShooting ? 100 : percent(countdown)}%` }}
            ></div>
            <div className={styles.progressBarText}>
              {isShooting
                ? languages.dermatoVideoCaptureShooting
                : languages.dermatoVideoCaptureInProgress(countdown)}
            </div>
          </Button>
        )}

        {step.status === "tutorial" && (
          <Button
            type="primary"
            onClick={startCountdown}
            disabled={!isDeviceReady}
            loading={!isDeviceReady}
          >
            {languages.dermatoVideoCaptureReady}
          </Button>
        )}
        {step.status === "select" && (
          <>
            <Button onClick={retakePhoto}>
              {languages.dermatoVideoCaptureSelectRetake}
            </Button>
            <Button
              type="primary"
              onClick={sendPhoto}
              disabled={submitDocumentLoading}
            >
              {languages.dermatoVideoCaptureSelectValidate}
            </Button>
          </>
        )}
        {step.status === "sent" && (
          <>
            <Button onClick={retakePhoto}>
              {languages.dermatoVideoCaptureTakeAnother}
            </Button>
            <Button type="primary" onClick={finishStep}>
              {languages.next}
            </Button>
          </>
        )}
      </Buttons>
      {step.status === "tutorial" && (
        <span className="small-link" onClick={finishStep}>
          {languages.dermatoVideoCaptureSkip}
        </span>
      )}
    </Card>
  )
}

export default DermatoscopeVideo
